import { useCallback, useRef, useState } from "react";
import { createApiEndpoint } from "../index.js";

const OUTPUT_TOGGLE_TIMEOUT_MS = 45000;
const OUTPUT_TOGGLE_SETTLE_MS = 3000;

function parseJsonPayload(raw) {
    let parsed = raw;
    for (let depth = 0; depth < 3 && typeof parsed === "string"; depth += 1) {
        try {
            parsed = JSON.parse(parsed);
        } catch {
            return null;
        }
    }
    return parsed && typeof parsed === "object" ? parsed : null;
}

function outputPendingKey(moduleId, index) {
    return `${Number(moduleId)}:${Number(index)}`;
}

function outputIsOn(modulePoints, moduleId, index) {
    if (!Array.isArray(modulePoints)) return false;
    const rec = modulePoints.find((m) => Number(m?.id) === Number(moduleId));
    const points = rec?.points;
    if (!Array.isArray(points) || index < 0 || index >= points.length) return false;
    return (Number(points[index]) || 0) !== 0;
}

function serverPendingKey(session) {
    const pt = session?.pendingToggle;
    return pt?.moduleId != null && pt?.index != null
        ? outputPendingKey(pt.moduleId, pt.index)
        : null;
}

// The server snapshot flips the value optimistically as soon as a toggle is saved, so a
// matching value only means "done" once the server has released its pendingToggle.
function settleOutputTogglePendingList(list, modulePoints, serverKey, now) {
    const next = [];
    for (const entry of list || []) {
        if (entry.sentAt == null || !entry.acked) {
            if (entry.sentAt != null && now - entry.sentAt > OUTPUT_TOGGLE_TIMEOUT_MS) continue;
            next.push(entry);
            continue;
        }
        if (serverKey === entry.key) {
            if (now - entry.sentAt > OUTPUT_TOGGLE_TIMEOUT_MS) continue;
            next.push(entry.releasedAt == null ? entry : { ...entry, releasedAt: null });
            continue;
        }
        if (outputIsOn(modulePoints, entry.moduleId, entry.index) === entry.targetOn) continue;
        const releasedAt = entry.releasedAt ?? now;
        if (now - releasedAt > OUTPUT_TOGGLE_SETTLE_MS) continue;
        next.push(entry.releasedAt == null ? { ...entry, releasedAt } : entry);
    }
    return next;
}

/**
 * Module viewer DO toggles: every click shows its target color + spinner immediately, and
 * clicks are queued because the server holds a single pendingToggle at a time.
 */
export function useModuleOutputToggles({ lockedRef, onError }) {
    const [modulePoints, setModulePoints] = useState([]);
    const [pendingOutputToggles, setPendingOutputToggles] = useState([]);
    const pendingRef = useRef([]);
    const modulePointsRef = useRef([]);
    const serverPendingKeyRef = useRef(null);
    const serverPendingSinceRef = useRef(0);
    const inFlightRef = useRef(false);
    const lastAckRef = useRef(0);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const syncPending = useCallback((value) => {
        const list = value || [];
        pendingRef.current = list;
        setPendingOutputToggles(list);
    }, []);

    const setServerPendingKey = useCallback((key) => {
        if (key === serverPendingKeyRef.current) return;
        serverPendingKeyRef.current = key;
        serverPendingSinceRef.current = key ? Date.now() : 0;
    }, []);

    const removePending = useCallback((key) => {
        syncPending(pendingRef.current.filter((p) => p.key !== key));
    }, [syncPending]);

    const pumpRef = useRef(() => {});
    const pump = useCallback(() => {
        if (lockedRef?.current || inFlightRef.current) return;
        const now = Date.now();
        if (
            serverPendingKeyRef.current &&
            now - serverPendingSinceRef.current < OUTPUT_TOGGLE_TIMEOUT_MS
        ) {
            return;
        }

        let next = null;
        for (const entry of pendingRef.current) {
            if (entry.sentAt != null) continue;
            if (outputIsOn(modulePointsRef.current, entry.moduleId, entry.index) === entry.targetOn) {
                removePending(entry.key);
                continue;
            }
            next = entry;
            break;
        }
        if (!next) return;

        const key = next.key;
        syncPending(pendingRef.current.map((p) => (p.key === key ? { ...p, sentAt: now } : p)));
        inFlightRef.current = true;

        void (async () => {
            try {
                const response = await createApiEndpoint().fetchAddDiagnostic(
                    "direct",
                    JSON.stringify({
                        action: "toggleOutput",
                        moduleId: next.moduleId,
                        index: next.index,
                    }),
                );
                const session = parseJsonPayload(response?.data?.addDiagnostic);
                if (!session || session.error) {
                    removePending(key);
                    if (session?.error) onErrorRef.current?.(session.error);
                    return;
                }
                lastAckRef.current = Date.now();
                setServerPendingKey(serverPendingKey(session));
                if (Array.isArray(session.modulePoints) && session.modulePoints.length > 0) {
                    modulePointsRef.current = session.modulePoints;
                    setModulePoints(session.modulePoints);
                }
                syncPending(pendingRef.current.map((p) => (p.key === key ? { ...p, acked: true } : p)));
            } catch (err) {
                removePending(key);
                console.warn("useModuleOutputToggles: toggle output", err);
            } finally {
                inFlightRef.current = false;
                pumpRef.current();
            }
        })();
    }, [lockedRef, removePending, setServerPendingKey, syncPending]);
    pumpRef.current = pump;

    /** Feed each getDiagnostic poll; `requestedAt` is when that poll request was sent. */
    const applyPolledSession = useCallback((session, requestedAt) => {
        // A poll that left before the last toggle was acknowledged carries a stale pendingToggle.
        if (!session || requestedAt < lastAckRef.current) return;
        if (Array.isArray(session.modulePoints)) {
            modulePointsRef.current = session.modulePoints;
            setModulePoints(session.modulePoints);
        }
        const serverKey = serverPendingKey(session);
        setServerPendingKey(serverKey);
        if (pendingRef.current.length > 0) {
            syncPending(
                settleOutputTogglePendingList(
                    pendingRef.current,
                    modulePointsRef.current,
                    serverKey,
                    Date.now(),
                ),
            );
            pump();
        }
    }, [pump, setServerPendingKey, syncPending]);

    const toggleOutput = useCallback((moduleId, index) => {
        if (lockedRef?.current) return;
        const mid = Number(moduleId);
        const idx = Number(index);
        const key = outputPendingKey(mid, idx);
        if (pendingRef.current.some((p) => p.key === key)) return;

        syncPending([
            ...pendingRef.current,
            {
                key,
                moduleId: mid,
                index: idx,
                targetOn: !outputIsOn(modulePointsRef.current, mid, idx),
                sentAt: null,
                acked: false,
                releasedAt: null,
            },
        ]);
        pump();
    }, [lockedRef, pump, syncPending]);

    const clearPendingOutputToggles = useCallback(() => {
        syncPending([]);
    }, [syncPending]);

    return {
        modulePoints,
        pendingOutputToggles,
        toggleOutput,
        applyPolledSession,
        clearPendingOutputToggles,
    };
}
